/**
 * AttesterOverride.js
 *
 * WHAT: Account owner (attester override) management for mapping database accounts to designated reviewers.
 * WHY:  Not all database accounts have a clear enterprise manager (e.g., service accounts, shared
 *       accounts). This component lets admins assign specific attesters to individual database
 *       accounts, overriding the default manager-based assignment. These overrides are applied
 *       during Step 3 (attester assignment) of every campaign, ensuring the right person
 *       reviews each account regardless of enterprise hierarchy.
 *
 * Note: The component name "AttesterOverride" and related technical names (variables,
 * API endpoints) use "override" terminology for backward compatibility, but all
 * user-facing text displays "Account Owner" to reflect the actual business concept.
 */
import React, { useState, useEffect } from 'react';
import SearchableDropdown from './SearchableDropdown';
import { WideModal } from './common';

import API_CONFIG from '../config/api.config';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || API_CONFIG.getEndpoint('/api/databases');

/**
 * WHAT: CRUD interface for account owner assignments with individual and bulk assignment modes.
 * WHY:  Service and application accounts often have dozens of instances across databases.
 *       The bulk assignment mode lets admins assign an owner to all accounts on a database
 *       at once, while individual mode handles one-off overrides.
 */
const AttesterOverride = () => {
  const [overrides, setOverrides] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingOverride, setEditingOverride] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    database_name: '',
    username: '',
    is_active: ''
  });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [enterpriseUsers, setEnterpriseUsers] = useState([]);
  const [databases, setDatabases] = useState([]);
  const [databaseUsers, setDatabaseUsers] = useState([]);
  const [loadingDatabaseUsers, setLoadingDatabaseUsers] = useState(false);

  // Bulk assignment state
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkAccounts, setBulkAccounts] = useState([]);
  const [selectedBulkAccounts, setSelectedBulkAccounts] = useState([]);
  const [bulkFormData, setBulkFormData] = useState({
    database_type: '',
    database_name: '',
    attester_id: '',
    reason_code: 'ACCOUNT_OWNER',
    reason_description: 'Application/Service account owner assignment'
  });
  const [loadingBulkAccounts, setLoadingBulkAccounts] = useState(false);

  // Form state for create/edit
  const [formData, setFormData] = useState({
    database_name: '',
    database_type: '',
    username: '',
    attester_id: '',
    reason_code: '',
    reason_description: '',
    effective_date: '',
    expiry_date: '',
    is_active: 'true',
    notes: ''
  });

  // Reason codes
  const reasonCodes = [
    'ACCOUNT_OWNER',
    'MANAGER_CHANGE',
    'DEPT_RESTRUCTURE',
    'TEMPORARY_ASSIGNMENT',
    'SPECIAL_PROJECT',
    'COMPLIANCE_REQUIREMENT',
    'DELEGATION',
    'OTHER'
  ];

  useEffect(() => {
    loadOverrides();
  }, [filters]);

  useEffect(() => {
    loadEnterpriseUsers();
    loadDatabases();
  }, []);

  const loadOverrides = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filters.database_name) params.append('database_name', filters.database_name);
      if (filters.username) params.append('username', filters.username);
      if (filters.is_active) params.append('is_active', filters.is_active);

      const response = await fetch(API_CONFIG.getDatabasesEndpoint(`attester-overrides/?${params.toString()}`));
      const data = await response.json();

      if (data.success) {
        setOverrides(data.overrides);
      } else {
        setError('Failed to load account owners');
      }
    } catch (err) {
      setError('Error loading account owners: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const loadEnterpriseUsers = async () => {
    try {
      // Fetch all enterprise users with a large page size to avoid pagination issues
      const response = await fetch(API_CONFIG.getEnterpriseUsersEndpoint('?page_size=1000'));
      const data = await response.json();

      // API returns paginated results with 'results' array
      if (data.results) {
        setEnterpriseUsers(data.results);
      } else if (data.success) {
        // Fallback for non-paginated response format
        setEnterpriseUsers(data.users);
      } else {
        console.error('Failed to load enterprise users');
      }
    } catch (err) {
      console.error('Error loading enterprise users:', err.message);
    }
  };

  const loadDatabases = async () => {
    try {
      const response = await fetch(API_CONFIG.getDatabasesEndpoint('database-owners/databases-list/'));
      const data = await response.json();

      if (data.success) {
        setDatabases(data.databases);
      } else {
        console.error('Failed to load databases');
      }
    } catch (err) {
      console.error('Error loading databases:', err.message);
    }
  };

  const loadDatabaseUsers = async (databaseType, databaseName = '') => {
    if (!databaseType) {
      setDatabaseUsers([]);
      return;
    }

    setLoadingDatabaseUsers(true);
    try {
      const params = new URLSearchParams({ database_type: databaseType });
      if (databaseName && databaseName !== 'ALL') {
        params.append('database_name', databaseName);
      }

      const response = await fetch(API_CONFIG.getDatabasesEndpoint(`attester-overrides/non-human-users/?${params.toString()}`));
      const data = await response.json();

      if (data.success) {
        setDatabaseUsers(data.users);
      } else {
        console.error('Failed to load database users:', data.error);
        setDatabaseUsers([]);
      }
    } catch (err) {
      console.error('Error loading database users:', err.message);
      setDatabaseUsers([]);
    } finally {
      setLoadingDatabaseUsers(false);
    }
  };

  // Load bulk accounts for bulk assignment modal
  const loadBulkAccounts = async (databaseType, databaseName) => {
    if (!databaseType) {
      setBulkAccounts([]);
      setSelectedBulkAccounts([]);
      return;
    }

    setLoadingBulkAccounts(true);
    try {
      const params = new URLSearchParams({ database_type: databaseType });
      if (databaseName && databaseName !== 'ALL') {
        params.append('database_name', databaseName);
      }

      const response = await fetch(API_CONFIG.getDatabasesEndpoint(`attester-overrides/non-human-users/?${params.toString()}`));
      const data = await response.json();

      if (data.success) {
        setBulkAccounts(data.users);
        // Pre-select service/application accounts
        const serviceAccounts = data.users
          .filter(acc => acc.account_type === 'Service Account' || acc.account_type === 'Application Service Account')
          .map(acc => `${acc.username}|${acc.database_name}`);
        setSelectedBulkAccounts(serviceAccounts);
      } else {
        console.error('Failed to load bulk accounts:', data.error);
        setBulkAccounts([]);
      }
    } catch (err) {
      console.error('Error loading bulk accounts:', err.message);
      setBulkAccounts([]);
    } finally {
      setLoadingBulkAccounts(false);
    }
  };

  const handleBulkAccountToggle = (username, databaseName) => {
    const key = `${username}|${databaseName}`;
    setSelectedBulkAccounts(prev => {
      if (prev.includes(key)) {
        return prev.filter(k => k !== key);
      } else {
        return [...prev, key];
      }
    });
  };

  const handleSelectAllServiceAccounts = () => {
    const serviceAccounts = bulkAccounts
      .filter(acc => acc.account_type === 'Service Account' || acc.account_type === 'Application Service Account')
      .map(acc => `${acc.username}|${acc.database_name}`);
    setSelectedBulkAccounts(serviceAccounts);
  };

  const handleSelectAllBulkAccounts = () => {
    const allAccounts = bulkAccounts.map(acc => `${acc.username}|${acc.database_name}`);
    setSelectedBulkAccounts(allAccounts);
  };

  const handleDeselectAllBulkAccounts = () => {
    setSelectedBulkAccounts([]);
  };

  const handleBulkCreate = async () => {
    if (!bulkFormData.attester_id || selectedBulkAccounts.length === 0) {
      setError('Please select an attester and at least one account');
      return;
    }

    try {
      const accounts = selectedBulkAccounts.map(key => {
        const [username, database_name] = key.split('|');
        return { username, database_name };
      });

      const response = await fetch(API_CONFIG.getDatabasesEndpoint('attester-overrides/bulk-create/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          database_type: bulkFormData.database_type,
          attester_id: bulkFormData.attester_id,
          accounts: accounts,
          reason_code: bulkFormData.reason_code,
          reason_description: bulkFormData.reason_description,
          created_by: 'ADMIN_USER'
        }),
      });

      const data = await response.json();

      if (data.success) {
        setShowBulkModal(false);
        setBulkFormData({
          database_type: '',
          database_name: '',
          attester_id: '',
          reason_code: 'ACCOUNT_OWNER',
          reason_description: 'Application/Service account owner assignment'
        });
        setBulkAccounts([]);
        setSelectedBulkAccounts([]);
        loadOverrides();
        alert(`Bulk assignment complete!\nCreated: ${data.created}\nSkipped (already assigned): ${data.skipped}`);
      } else {
        setError(data.error || 'Failed to bulk create account owners');
      }
    } catch (err) {
      setError('Error bulk creating account owners: ' + err.message);
    }
  };

  // Get unique database types from databases
  const getUniqueDatabaseTypes = () => {
    // Normalize to uppercase to handle inconsistent casing (e.g., "ORACLE" vs "Oracle")
    const types = [...new Set(databases.map(db => db.database_type?.toUpperCase()).filter(Boolean))];
    return types.sort();
  };

  // Get databases filtered by selected database_type
  const getFilteredDatabases = () => {
    if (!formData.database_type) {
      return databases;
    }
    // Case-insensitive comparison
    return databases.filter(db => db.database_type?.toUpperCase() === formData.database_type.toUpperCase());
  };

  // Format database option for dropdown.
  // M:N: one option per database; show ALL linked app names so searching by any
  // linked app (not just the primary) still finds the database.
  const formatDatabaseOption = (db) => {
    const apps = db.application_names || (db.app_id ? `${db.app_id} - ${db.app_name}` : '');
    return apps ? `${db.database_name} (${apps})` : db.database_name;
  };

  const handleCreateOverride = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(API_CONFIG.getDatabasesEndpoint('attester-overrides/create/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          created_by: 'ADMIN_USER'
        }),
      });

      const data = await response.json();

      if (data.success) {
        setShowCreateModal(false);
        setFormData({
          database_name: '',
          database_type: '',
          username: '',
          attester_id: '',
          reason_code: '',
          reason_description: '',
          effective_date: '',
          expiry_date: '',
          is_active: 'true',
          notes: ''
        });
        loadOverrides();
      } else {
        setError(data.error || 'Failed to create account owner');
      }
    } catch (err) {
      setError('Error creating account owner: ' + err.message);
    }
  };

  const handleEditOverride = async (e) => {
    e.preventDefault();
    try {
      const overrideId = `${editingOverride.database_name}|${editingOverride.database_type}|${editingOverride.username}`;
      const response = await fetch(API_CONFIG.getDatabasesEndpoint(`attester-overrides/${encodeURIComponent(overrideId)}/update/`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          last_modified_by: 'ADMIN_USER'
        }),
      });

      const data = await response.json();

      if (data.success) {
        setShowEditModal(false);
        setEditingOverride(null);
        loadOverrides();
      } else {
        setError(data.error || 'Failed to update account owner');
      }
    } catch (err) {
      setError('Error updating account owner: ' + err.message);
    }
  };

  const handleDeleteOverride = async (override) => {
    if (!window.confirm('Are you sure you want to delete this account owner?')) {
      return;
    }

    try {
      const overrideId = `${override.database_name}|${override.database_type}|${override.username}`;
      const response = await fetch(API_CONFIG.getDatabasesEndpoint(`attester-overrides/${encodeURIComponent(overrideId)}/delete/`), {
        method: 'DELETE',
      });

      const data = await response.json();

      if (data.success) {
        loadOverrides();
      } else {
        setError(data.error || 'Failed to delete account owner');
      }
    } catch (err) {
      setError('Error deleting account owner: ' + err.message);
    }
  };

  const openEditModal = (override) => {
    setEditingOverride(override);
    setFormData({
      database_name: override.database_name,
      database_type: override.database_type,
      username: override.username,
      attester_id: override.attester_id,
      reason_code: override.reason_code,
      reason_description: override.reason_description || '',
      effective_date: override.effective_date ? override.effective_date.split('T')[0] : '',
      expiry_date: override.expiry_date ? override.expiry_date.split('T')[0] : '',
      is_active: override.is_active,
      notes: override.notes || ''
    });
    setShowEditModal(true);
  };

  const resetCreateForm = () => {
    setFormData({
      database_name: '',
      database_type: '',
      username: '',
      attester_id: '',
      reason_code: '',
      reason_description: '',
      effective_date: '',
      expiry_date: '',
      is_active: 'true',
      notes: ''
    });
  };

  // CSV Export
  const handleExportCSV = async () => {
    try {
      const response = await fetch(API_CONFIG.getDatabasesEndpoint('attester-overrides/export-csv/'));
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'account_owners_export.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Error exporting CSV: ' + err.message);
    }
  };

  // CSV Import
  const handleImportCSV = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(API_CONFIG.getDatabasesEndpoint('attester-overrides/import-csv/'), {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        alert(`Import successful!\nAdded: ${data.added}\nUpdated: ${data.updated}`);
        loadOverrides();
      } else {
        setError(data.error || 'Import failed');
      }
    } catch (err) {
      setError('Error importing CSV: ' + err.message);
    }

    // Reset file input
    event.target.value = '';
  };

  // Helper function to get attester name
  const getAttesterName = (attesterId) => {
    const user = enterpriseUsers.find(u => u.employee_id === attesterId);
    return user ? user.display_name : '';
  };

  // Sorting function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Sort and paginate overrides
  const getSortedOverrides = () => {
    let sortedData = [...overrides];

    if (sortConfig.key) {
      sortedData.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle attester name sorting
        if (sortConfig.key === 'attester_name') {
          aValue = getAttesterName(a.attester_id);
          bValue = getAttesterName(b.attester_id);
        }

        // Handle date sorting
        if (sortConfig.key === 'effective_date' || sortConfig.key === 'expiry_date') {
          aValue = aValue ? new Date(aValue).getTime() : 0;
          bValue = bValue ? new Date(bValue).getTime() : 0;
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return sortedData;
  };

  const sortedOverrides = getSortedOverrides();
  const totalPages = Math.ceil(sortedOverrides.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentOverrides = sortedOverrides.slice(startIndex, endIndex);

  // Sort icon helper
  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <i className="fas fa-sort text-muted ms-1"></i>;
    }
    return sortConfig.direction === 'asc' ?
      <i className="fas fa-sort-up ms-1"></i> :
      <i className="fas fa-sort-down ms-1"></i>;
  };

  return (
    <div className="container-fluid mt-4">
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <i className="fas fa-user-shield me-2"></i>
                Account Owners
                <span className="badge bg-secondary ms-2">
                  {overrides.length}
                </span>
              </h5>
              <div>
                <button
                  className="btn btn-sm btn-outline-primary me-2"
                  onClick={loadOverrides}
                  disabled={isLoading}
                >
                  <i className="fas fa-sync-alt me-2"></i>
                  Refresh
                </button>
                <button
                  className="btn btn-sm btn-outline-success me-2"
                  onClick={handleExportCSV}
                >
                  <i className="fas fa-download me-2"></i>
                  Download CSV
                </button>
                <label className="btn btn-sm btn-outline-info me-2" style={{ marginBottom: 0 }}>
                  <i className="fas fa-upload me-2"></i>
                  Upload CSV
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleImportCSV}
                    style={{ display: 'none' }}
                  />
                </label>
                <button
                  className="btn btn-sm btn-warning me-2"
                  onClick={() => {
                    setBulkFormData({
                      database_type: '',
                      database_name: '',
                      attester_id: '',
                      reason_code: 'ACCOUNT_OWNER',
                      reason_description: 'Application/Service account owner assignment'
                    });
                    setBulkAccounts([]);
                    setSelectedBulkAccounts([]);
                    setShowBulkModal(true);
                  }}
                >
                  <i className="fas fa-users-cog me-2"></i>
                  Bulk Assign
                </button>
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => {
                    resetCreateForm();
                    setShowCreateModal(true);
                  }}
                >
                  <i className="fas fa-plus me-2"></i>
                  Create Account Owner
                </button>
              </div>
            </div>

            {/* Filters */}
            <div className="card-body border-bottom bg-light">
              <div className="row g-3">
                <div className="col-md-3">
                  <label className="form-label fw-bold">Database Name</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Search database name..."
                    value={filters.database_name}
                    onChange={(e) => setFilters(prev => ({ ...prev, database_name: e.target.value }))}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-bold">Username</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Search username..."
                    value={filters.username}
                    onChange={(e) => setFilters(prev => ({ ...prev, username: e.target.value }))}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-bold">Status</label>
                  <select
                    className="form-select form-select-sm"
                    value={filters.is_active}
                    onChange={(e) => setFilters(prev => ({ ...prev, is_active: e.target.value }))}
                  >
                    <option value="">All Status</option>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
                <div className="col-md-3 d-flex align-items-end">
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setFilters({ database_name: '', username: '', is_active: '' })}
                  >
                    <i className="fas fa-times me-2"></i>
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="card-body">
                <div className="alert alert-danger mb-0" role="alert">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {error}
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setError(null)}
                  ></button>
                </div>
              </div>
            )}

            {/* Main Table */}
            <div className="card-body">
              {isLoading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <div className="mt-2">Loading account owners...</div>
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table table-striped table-hover">
                      <thead className="table-dark">
                        <tr>
                          <th style={{ cursor: 'pointer' }} onClick={() => handleSort('database_name')}>
                            Database {getSortIcon('database_name')}
                          </th>
                          <th style={{ cursor: 'pointer' }} onClick={() => handleSort('username')}>
                            Username {getSortIcon('username')}
                          </th>
                          <th style={{ cursor: 'pointer' }} onClick={() => handleSort('attester_id')}>
                            Employee ID {getSortIcon('attester_id')}
                          </th>
                          <th style={{ cursor: 'pointer' }} onClick={() => handleSort('attester_name')}>
                            Attester Name {getSortIcon('attester_name')}
                          </th>
                          <th style={{ cursor: 'pointer' }} onClick={() => handleSort('reason_code')}>
                            Reason {getSortIcon('reason_code')}
                          </th>
                          <th style={{ cursor: 'pointer' }} onClick={() => handleSort('effective_date')}>
                            Effective Date {getSortIcon('effective_date')}
                          </th>
                          <th style={{ cursor: 'pointer' }} onClick={() => handleSort('expiry_date')}>
                            Expiry Date {getSortIcon('expiry_date')}
                          </th>
                          <th style={{ cursor: 'pointer' }} onClick={() => handleSort('is_active')}>
                            Status {getSortIcon('is_active')}
                          </th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentOverrides.length === 0 ? (
                          <tr>
                            <td colSpan="9" className="text-center py-4">
                              <i className="fas fa-inbox text-muted me-2"></i>
                              No account owners found
                            </td>
                          </tr>
                        ) : (
                          currentOverrides.map((override, index) => (
                            <tr key={index}>
                              <td>
                                <div className="fw-semibold">{override.database_name}</div>
                                <small className="text-muted">{override.database_type}</small>
                              </td>
                              <td>
                                <code>{override.username}</code>
                              </td>
                              <td>
                                <div>
                                  <span className="text-info">{override.attester_id}</span>
                                  {override.attester_job_title && (
                                    <div><small className="text-muted">{override.attester_job_title}</small></div>
                                  )}
                                  {override.attester_department && (
                                    <div><small className="text-muted">{override.attester_department}</small></div>
                                  )}
                                </div>
                              </td>
                              <td>
                                {override.attester_name ? (
                                  <div>
                                    <div className="fw-semibold">{override.attester_name}</div>
                                    <small className="text-muted">{override.attester_email}</small>
                                  </div>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                              <td>
                                <div className="fw-semibold">{override.reason_code}</div>
                                {override.reason_description && (
                                  <small className="text-muted">{override.reason_description}</small>
                                )}
                              </td>
                              <td>
                                {override.effective_date ? new Date(override.effective_date).toLocaleDateString() : '-'}
                              </td>
                              <td>
                                {override.expiry_date ? new Date(override.expiry_date).toLocaleDateString() : 'No expiry'}
                              </td>
                              <td>
                                <span className={override.is_active === 'true' ? 'text-success' : 'text-secondary'}>
                                  {override.is_active === 'true' ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td>
                                <div className="btn-group btn-group-sm">
                                  <button
                                    className="btn btn-outline-primary"
                                    onClick={() => openEditModal(override)}
                                    title="Edit Account Owner"
                                  >
                                    <i className="fas fa-edit"></i>
                                  </button>
                                  <button
                                    className="btn btn-outline-danger"
                                    onClick={() => handleDeleteOverride(override)}
                                    title="Delete Account Owner"
                                  >
                                    <i className="fas fa-trash"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <nav aria-label="Pagination">
                      <ul className="pagination justify-content-center mt-3">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => setCurrentPage(currentPage - 1)}
                            disabled={currentPage === 1}
                          >
                            Previous
                          </button>
                        </li>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                          <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => setCurrentPage(pageNum)}
                            >
                              {pageNum}
                            </button>
                          </li>
                        ))}
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => setCurrentPage(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          >
                            Next
                          </button>
                        </li>
                      </ul>
                    </nav>
                  )}

                  {/* Summary */}
                  {overrides.length > 0 && (
                    <div className="mt-3">
                      <small className="text-muted">
                        <i className="fas fa-info-circle me-1"></i>
                        Showing {overrides.length} account owner{overrides.length !== 1 ? 's' : ''}
                      </small>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Create Modal */}
      <WideModal
        show={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create Account Owner"
        headerIcon="fa-plus"
        footer={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowCreateModal(false)}
            >
              Cancel
            </button>
            <button type="submit" form="createAccountOwnerForm" className="btn btn-primary">
              <i className="fas fa-save me-2"></i>
              Create Account Owner
            </button>
          </>
        }
      >
        <form id="createAccountOwnerForm" onSubmit={handleCreateOverride}>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Database Type *</label>
              <select
                className="form-select"
                value={formData.database_type}
                onChange={(e) => {
                  const newType = e.target.value;
                  setFormData(prev => ({
                    ...prev,
                    database_type: newType,
                    database_name: '',  // Clear database_name when type changes
                    username: ''  // Clear username when type changes
                  }));
                  setDatabaseUsers([]);  // Clear users list
                  if (newType) {
                    loadDatabaseUsers(newType);
                  }
                }}
                required
              >
                <option value="">Select type...</option>
                {getUniqueDatabaseTypes().map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label">Database Name *</label>
              <select
                className="form-select"
                value={formData.database_name}
                onChange={(e) => {
                  const newDbName = e.target.value;
                  setFormData(prev => ({ ...prev, database_name: newDbName, username: '' }));
                  if (formData.database_type) {
                    loadDatabaseUsers(formData.database_type, newDbName);
                  }
                }}
                disabled={!formData.database_type}
                required
              >
                <option value="">Select database...</option>
                {formData.database_type && (
                  <>
                    <option value="ALL">ALL (All databases of this type)</option>
                    {getFilteredDatabases().map(db => (
                      <option key={`${db.database_name}-${db.database_type}`} value={db.database_name}>
                        {formatDatabaseOption(db)}
                      </option>
                    ))}
                  </>
                )}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label">Username * {loadingDatabaseUsers && <span className="text-muted">(Loading...)</span>}</label>
              {databaseUsers.length > 0 ? (
                <SearchableDropdown
                  options={databaseUsers.map(u => ({
                    username: u.username,
                    database_name: u.database_name,
                    account_type: u.account_type,
                    display_name: `${u.username} (${u.database_name}) - ${u.account_type}`
                  }))}
                  value={formData.username}
                  onChange={(value) => setFormData(prev => ({ ...prev, username: value }))}
                  placeholder="Type to search username..."
                  valueField="username"
                  displayField="display_name"
                  disabled={!formData.database_type}
                  required
                />
              ) : (
                <input
                  type="text"
                  className="form-control"
                  value={formData.username}
                  onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                  placeholder={formData.database_type ? "Enter username..." : "Select database type first..."}
                  disabled={!formData.database_type}
                  required
                />
              )}
              {formData.database_type && databaseUsers.length === 0 && !loadingDatabaseUsers && (
                <small className="text-muted">No non-human accounts found for this selection.</small>
              )}
            </div>
            <div className="col-md-6">
              <label className="form-label">Attester ID *</label>
              <SearchableDropdown
                options={enterpriseUsers}
                value={formData.attester_id}
                onChange={(value) => setFormData(prev => ({ ...prev, attester_id: value }))}
                placeholder="Type to search attester..."
                valueField="employee_id"
                required
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Reason Code *</label>
              <select
                className="form-select"
                value={formData.reason_code}
                onChange={(e) => setFormData(prev => ({ ...prev, reason_code: e.target.value }))}
                required
              >
                <option value="">Select reason...</option>
                {reasonCodes.map(code => (
                  <option key={code} value={code}>{code.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={formData.is_active}
                onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.value }))}
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label">Effective Date</label>
              <input
                type="date"
                className="form-control"
                value={formData.effective_date}
                onChange={(e) => setFormData(prev => ({ ...prev, effective_date: e.target.value }))}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Expiry Date</label>
              <input
                type="date"
                className="form-control"
                value={formData.expiry_date}
                onChange={(e) => setFormData(prev => ({ ...prev, expiry_date: e.target.value }))}
              />
            </div>
            <div className="col-12">
              <label className="form-label">Reason Description</label>
              <textarea
                className="form-control"
                rows="3"
                value={formData.reason_description}
                onChange={(e) => setFormData(prev => ({ ...prev, reason_description: e.target.value }))}
                placeholder="Optional detailed description..."
              />
            </div>
            <div className="col-12">
              <label className="form-label">Notes</label>
              <textarea
                className="form-control"
                rows="2"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Optional additional notes..."
              />
            </div>
          </div>
        </form>
      </WideModal>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-edit me-2"></i>
                  Edit Account Owner
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowEditModal(false)}
                ></button>
              </div>
              <form onSubmit={handleEditOverride}>
                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Database Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.database_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, database_name: e.target.value }))}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Database Type *</label>
                      <select
                        className="form-select"
                        value={formData.database_type}
                        onChange={(e) => setFormData(prev => ({ ...prev, database_type: e.target.value }))}
                        required
                      >
                        <option value="">Select type...</option>
                        <option value="ORACLE">Oracle</option>
                        <option value="MYSQL">MySQL</option>
                        <option value="POSTGRESQL">PostgreSQL</option>
                        <option value="SQLSERVER">SQL Server</option>
                        <option value="MONGODB">MongoDB</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Username</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.username}
                        disabled
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Attester ID *</label>
                      <SearchableDropdown
                        options={enterpriseUsers}
                        value={formData.attester_id}
                        onChange={(value) => setFormData(prev => ({ ...prev, attester_id: value }))}
                        placeholder="Type to search attester..."
                        valueField="employee_id"
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Reason Code *</label>
                      <select
                        className="form-select"
                        value={formData.reason_code}
                        onChange={(e) => setFormData(prev => ({ ...prev, reason_code: e.target.value }))}
                        required
                      >
                        <option value="">Select reason...</option>
                        {reasonCodes.map(code => (
                          <option key={code} value={code}>{code.replace(/_/g, ' ')}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Status</label>
                      <select
                        className="form-select"
                        value={formData.is_active}
                        onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.value }))}
                      >
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Effective Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={formData.effective_date}
                        onChange={(e) => setFormData(prev => ({ ...prev, effective_date: e.target.value }))}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Expiry Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={formData.expiry_date}
                        onChange={(e) => setFormData(prev => ({ ...prev, expiry_date: e.target.value }))}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Reason Description</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={formData.reason_description}
                        onChange={(e) => setFormData(prev => ({ ...prev, reason_description: e.target.value }))}
                        placeholder="Optional detailed description..."
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Notes</label>
                      <textarea
                        className="form-control"
                        rows="2"
                        value={formData.notes}
                        onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                        placeholder="Optional additional notes..."
                      />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowEditModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    <i className="fas fa-save me-2"></i>
                    Update Account Owner
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Assign Modal */}
      {showBulkModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1050, overflow: 'auto' }}>
          <div style={{ padding: '20px' }}>
            <div className="modal-content" style={{ maxWidth: '1400px', width: '100%', margin: '0 auto' }}>
              <div className="modal-header bg-warning text-dark">
                <h5 className="modal-title">
                  <i className="fas fa-users-cog me-2"></i>
                  Bulk Assign Account Owners
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowBulkModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row g-3 mb-4">
                  <div className="col-md-3">
                    <label className="form-label">Database Type *</label>
                    <select
                      className="form-select"
                      value={bulkFormData.database_type}
                      onChange={(e) => {
                        const newType = e.target.value;
                        setBulkFormData(prev => ({ ...prev, database_type: newType, database_name: '' }));
                        setBulkAccounts([]);
                        setSelectedBulkAccounts([]);
                        if (newType) {
                          loadBulkAccounts(newType, '');
                        }
                      }}
                    >
                      <option value="">Select type...</option>
                      {getUniqueDatabaseTypes().map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Database Name</label>
                    <select
                      className="form-select"
                      value={bulkFormData.database_name}
                      onChange={(e) => {
                        const newDbName = e.target.value;
                        setBulkFormData(prev => ({ ...prev, database_name: newDbName }));
                        loadBulkAccounts(bulkFormData.database_type, newDbName);
                      }}
                      disabled={!bulkFormData.database_type}
                    >
                      <option value="">All databases</option>
                      {databases
                        .filter(db => db.database_type?.toUpperCase() === bulkFormData.database_type?.toUpperCase())
                        .map((db, index) => (
                          <option key={index} value={db.database_name}>
                            {formatDatabaseOption(db)}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Attester *</label>
                    <SearchableDropdown
                      options={enterpriseUsers}
                      value={bulkFormData.attester_id}
                      onChange={(value) => setBulkFormData(prev => ({ ...prev, attester_id: value }))}
                      placeholder="Type to search attester..."
                      valueField="employee_id"
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Reason Code *</label>
                    <select
                      className="form-select"
                      value={bulkFormData.reason_code}
                      onChange={(e) => setBulkFormData(prev => ({ ...prev, reason_code: e.target.value }))}
                    >
                      {reasonCodes.map(code => (
                        <option key={code} value={code}>{code.replace(/_/g, ' ')}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12">
                    <label className="form-label">Reason Description</label>
                    <input
                      type="text"
                      className="form-control"
                      value={bulkFormData.reason_description}
                      onChange={(e) => setBulkFormData(prev => ({ ...prev, reason_description: e.target.value }))}
                      placeholder="Enter the reason for this bulk assignment..."
                    />
                  </div>
                </div>

                {/* Account Selection */}
                {bulkFormData.database_type && (
                  <div className="card">
                    <div className="card-header bg-secondary text-white d-flex justify-content-between align-items-center">
                      <h6 className="mb-0">
                        <i className="fas fa-users me-2"></i>
                        Select Accounts ({selectedBulkAccounts.length} of {bulkAccounts.length} selected)
                      </h6>
                      <div className="btn-group btn-group-sm">
                        <button
                          type="button"
                          className="btn btn-outline-light"
                          onClick={handleSelectAllServiceAccounts}
                          title="Select all service/application accounts"
                        >
                          <i className="fas fa-robot me-1"></i>
                          Service Only
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-light"
                          onClick={handleSelectAllBulkAccounts}
                          title="Select all accounts"
                        >
                          <i className="fas fa-check-double me-1"></i>
                          All
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-light"
                          onClick={handleDeselectAllBulkAccounts}
                          title="Deselect all accounts"
                        >
                          <i className="fas fa-times me-1"></i>
                          None
                        </button>
                      </div>
                    </div>
                    <div className="card-body p-0" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      {loadingBulkAccounts ? (
                        <div className="text-center py-4">
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <div className="mt-2">Loading accounts...</div>
                        </div>
                      ) : bulkAccounts.length === 0 ? (
                        <div className="text-center py-4 text-muted">
                          <i className="fas fa-inbox fa-2x mb-2"></i>
                          <div>No unassigned service/application accounts found</div>
                          <small>Accounts already assigned to an owner or database owner are excluded</small>
                        </div>
                      ) : (
                        <table className="table table-hover mb-0">
                          <thead className="table-dark sticky-top">
                            <tr>
                              <th style={{ width: '50px' }}>
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={selectedBulkAccounts.length === bulkAccounts.length && bulkAccounts.length > 0}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      handleSelectAllBulkAccounts();
                                    } else {
                                      handleDeselectAllBulkAccounts();
                                    }
                                  }}
                                />
                              </th>
                              <th>Username</th>
                              <th>Database</th>
                              <th>Account Type</th>
                            </tr>
                          </thead>
                          <tbody>
                            {bulkAccounts.map((account, index) => {
                              const key = `${account.username}|${account.database_name}`;
                              const isSelected = selectedBulkAccounts.includes(key);
                              return (
                                <tr
                                  key={index}
                                  className={isSelected ? 'table-primary' : ''}
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => handleBulkAccountToggle(account.username, account.database_name)}
                                >
                                  <td>
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                      checked={isSelected}
                                      onChange={() => handleBulkAccountToggle(account.username, account.database_name)}
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  </td>
                                  <td>
                                    <code>{account.username}</code>
                                  </td>
                                  <td>{account.database_name}</td>
                                  <td>
                                    {account.account_type === 'Service Account' || account.account_type === 'Application Service Account' ? (
                                      <span className="text-info">
                                        <i className="fas fa-robot me-1"></i>
                                        {account.account_type}
                                      </span>
                                    ) : account.account_type === 'System Account' ? (
                                      <span className="text-warning">
                                        <i className="fas fa-cog me-1"></i>
                                        {account.account_type}
                                      </span>
                                    ) : (
                                      <span className="text-muted">{account.account_type || 'Unknown'}</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                )}

                {!bulkFormData.database_type && (
                  <div className="alert alert-info">
                    <i className="fas fa-info-circle me-2"></i>
                    Please select a database type to view available service/application accounts.
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowBulkModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-warning"
                  onClick={handleBulkCreate}
                  disabled={!bulkFormData.attester_id || selectedBulkAccounts.length === 0}
                >
                  <i className="fas fa-save me-2"></i>
                  Assign {selectedBulkAccounts.length} Owner{selectedBulkAccounts.length !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttesterOverride;